import { BaseModel } from './base.model';
import { Expose, Transform, Type } from 'class-transformer';
import { USER_STATUS } from '../data';
import { IPhoneNumber, IUser, IUserAvailability, IUserProfile } from '../interfaces';
import { UserAvailabilityType, UserRole, UserStatusType } from '../types';
import { GroupModel, ViewGroupModel } from './group.model';
import { PhoneNumberObject, PhoneNumberString } from '../decorators';

export class UserAvailabilityModel extends BaseModel implements IUserAvailability {
  @Expose()
  id!: number;

  @Expose()
  status!: UserAvailabilityType;

  @Expose()
  userId!: number;
}

export class UserProfileModel extends BaseModel implements IUserProfile {
  @Expose()
  countryCode!: string;

  @Expose()
  phoneNumber!: string;

  @Expose()
  @PhoneNumberObject('countryCode', 'phoneNumber')
  phoneNumberObj!: IPhoneNumber;

  @Expose()
  @PhoneNumberString('countryCode', 'phoneNumber')
  fullPhoneNumber!: string;

  @Expose()
  secondEmail!: string;

  @Expose()
  ceaNumber!: string;

  @Expose()
  designation!: string;
}

export class UserModel extends BaseModel implements IUser {
  @Expose()
  id!: number;

  @Expose()
  firstName!: string;

  @Expose()
  lastName!: string;

  @Expose()
  @Transform(({ obj }) => `${obj.firstName} ${obj.lastName}`)
  fullName!: string;

  @Expose()
  @Transform(
    ({ obj }) => `${obj.firstName} ${obj.lastName} ${obj.status === USER_STATUS[1].value ? ' (Inactive)' : ''}`
  )
  fullNameWithStatus!: string;

  @Expose()
  username!: string;

  @Expose()
  code!: string;

  @Expose()
  email!: string;

  @Expose()
  avatar!: string | null;

  @Expose()
  phoneNumber!: string | null;

  @Expose()
  @Type(() => UserProfileModel)
  profile!: UserProfileModel;

  @Expose()
  status!: UserStatusType;

  @Expose()
  @Transform(({ obj }) => {
    const defaultValue = { value: obj.status, viewValue: obj.status };
    if (!obj.status) return defaultValue;

    const statusDetail = USER_STATUS.find(status => status.value === obj.status);
    if (!statusDetail) return defaultValue;

    return {
      value: obj.status,
      viewValue: statusDetail.viewValue,
      class: statusDetail.class,
    };
  })
  statusDisplay!: {
    value: UserStatusType;
    viewValue: string;
    class?: string;
  };

  @Expose()
  @Transform(({ obj }) => {
    return obj.status === USER_STATUS[1].value;
  })
  canActivate!: boolean;

  @Expose()
  @Transform(({ obj }) => {
    return obj.status !== USER_STATUS[1].value || obj.status === USER_STATUS[0].value;
  })
  canDeactivate!: boolean;

  @Expose()
  signature?: string;

  @Expose()
  activated!: string;

  @Expose()
  createdAt!: string;

  @Expose()
  lastLogin!: string;

  @Expose()
  updatedAt!: string;

  @Expose()
  @Type(() => GroupModel)
  groups!: GroupModel[];

  @Expose()
  @Type(() => ViewGroupModel)
  viewGroup!: ViewGroupModel[];

  @Expose()
  @Transform(({ obj }) => obj['viewGroup']?.[0]?.name ?? '')
  viewGroupDisplay!: string;

  @Expose()
  teamLeadId?: number;

  @Expose()
  teamName?: string;

  @Expose()
  teamLead?: IUser;

  // BE Stuff
  @Expose()
  ssoProvider!: string;

  @Expose()
  ssoUserId!: string;

  @Expose()
  forceChangePassword!: any;

  @Expose()
  lastChangePasswordAt!: string | null;

  @Expose()
  DN?: string;

  /* Role based permission.
   * Workflow role is derived from `group.role`, NOT `group.slug` and NOT the
   * legacy `group.tag[0]`: a permission-cut group (e.g. the pilot `rm-pilot`,
   * slug `rm-pilot`) carries `role: 'rm'` so it still behaves as its base
   * workflow role. Authorization itself is decided by permission slugs
   * elsewhere — this only resolves workflow identity. For every non-pilot group
   * `role === slug`, so this matches the previous behaviour. */
  @Expose()
  @Transform(({ obj }) => {
    const role = obj['groups']?.[0]?.role as UserRole;

    return {
      canGenerateLo: ['rm', 'rm-manager'].includes(role),
      canFilterCbsConsentPic: ['rm', 'rm-manager', 'rm-head'].includes(role),
    };
  })
  rolePermission!: {
    canGenerateLo: boolean;
    canFilterCbsConsentPic: boolean;
  };

  @Expose()
  @Transform(({ obj }) => {
    const role = obj['groups']?.[0]?.role as UserRole;

    return role?.startsWith('icimb') ?? false;
  })
  isInICimbGroup!: boolean;

  @Expose()
  @Transform(({ obj }) => {
    return obj['groups']?.[0]?.role as UserRole;
  })
  role!: UserRole;
}
